import { buildFinishedPartId } from '@calculations/utilities/build-finished-part-id';
import { PayloadAction } from '@reduxjs/toolkit';
import { SnapSuggestionStatus } from '@snap-types/models/common';
import { CraftProductOptionsOptionGroup } from '@snap-types/models/craft/product-options';
import { WritableDraft } from 'immer';

import { QuoteBuilderStateType } from '../../types/state';
import { onProductOptionChange } from '../calculations/recalculate/on-product-option-change';
import { getQuoteLineOrThrow } from '../utilities/action-guards';

interface Params {
  name: string;
  quoteLineUuid: string;
  productOptionUuid: string;
  craftProductOptionGroup: CraftProductOptionsOptionGroup;
}

export function productOptionSelected(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<Params>,
) {
  const { quoteLineUuid, name, productOptionUuid, craftProductOptionGroup } =
    action.payload;

  const quoteLine = getQuoteLineOrThrow(state, quoteLineUuid);

  const marketCode = state.marketCode;

  if (
    !marketCode ||
    !quoteLine.marketPrices ||
    !quoteLine.craftProductOptions ||
    !quoteLine.productOptions
  ) {
    return;
  }

  const selectedOption = craftProductOptionGroup.options.find(
    (option) => option.name === name,
  );

  if (!selectedOption) {
    return;
  }

  const partOption = quoteLine.productOptions[productOptionUuid];

  partOption.code = selectedOption.code;
  partOption.optionName = name;

  let changesUnitPrice = false;

  for (const quotePriceUuid of quoteLine.quotePriceUuids) {
    const price = onProductOptionChange(state, {
      quoteLineUuid,
      quotePriceUuid,
    });
    if (price.changesUnitPrice) {
      changesUnitPrice = true;
    }
  }

  quoteLine.productOptionsChangeUnitPrice = changesUnitPrice;
  quoteLine.suggestedPricesStatus = SnapSuggestionStatus.ACCEPTED;

  const allQuantitiesAutomated = Object.values(quoteLine.quotePrices).every(
    (quotePrice) => {
      return quotePrice.automatedQuantity?.eq(quotePrice.quantity);
    },
  );

  quoteLine.automatable =
    quoteLine.leadTimeSuggestionStatus === SnapSuggestionStatus.ACCEPTED &&
    allQuantitiesAutomated;

  const finishedPartId = buildFinishedPartId({
    partId: quoteLine.partId,
    craftProductOptions: quoteLine.craftProductOptions,
    productOptions: Object.values(quoteLine.productOptions),
  });

  if (!finishedPartId.isSuccess) {
    return;
  }

  quoteLine.finishedPartId = finishedPartId.finishedPartId;
}
